import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { fetchCustomerById, createCustomer, updateCustomer } from '../../redux/slices/customersSlice';
import { isCentralDispatchRole, isCentralRole, isOperatorAdminRole, isOperatorRole } from '../../redux/slices/authSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const isEditMode = !!id;
  
  const { currentCustomer, isLoading, error } = useSelector(state => state.customers);
  const currentUser = useSelector(state => state.auth.user);
  const customerManagingOperatorId = currentCustomer?.user?.managedByOperatorId ||
    currentCustomer?.user?.createdByOperatorId ||
    currentCustomer?.user?.operatorId;
  const canCreateCustomer = isCentralRole(currentUser) || isCentralDispatchRole(currentUser) || isOperatorRole(currentUser);
  const canEditCustomer = isCentralRole(currentUser) || (
    isOperatorAdminRole(currentUser) &&
    currentCustomer?.user?.isPhoneCustomer === true &&
    customerManagingOperatorId === currentUser?.operatorId
  );
  
  const [formData, setFormData] = useState({
    // Kunden-Informationen (nur für Telefonkunden im Admin-Panel)
    name: '',
    phoneNumber: '',
    address: '',
    isPhoneCustomer: true, // Immer true für neue Kunden
  });
  
  const [formErrors, setFormErrors] = useState({
    name: '',
    phoneNumber: '',
    address: '',
  });
  
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchCustomerById(id));
    }
  }, [dispatch, id, isEditMode]);
  
  useEffect(() => {
    if (isEditMode && currentCustomer) {
      // Beim Bearbeiten bestehender Kunden zeigen wir alle Daten an
      setFormData({
        ...formData,
        name: currentCustomer.user?.name || '',
        phoneNumber: currentCustomer.user?.phoneNumber || '',
        address: currentCustomer.user?.address || '',
        // isPhoneCustomer wird aus der DB übernommen, spielt beim Bearbeiten aber keine Rolle
        isPhoneCustomer: currentCustomer.user?.isPhoneCustomer || false,
      });
    }
  }, [currentCustomer, isEditMode]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Fehler zurücksetzen, wenn der Benutzer ein Feld ändert
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    // Bei allen Kunden validieren wir Name und Telefonnummer
    if (!formData.name) {
      newErrors.name = 'Name ist erforderlich';
      valid = false;
    }
    
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Telefonnummer ist erforderlich';
      valid = false;
    }
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();

    if ((!isEditMode && !canCreateCustomer) || (isEditMode && !canEditCustomer)) {
      return;
    }
    
    if (validateForm()) {
      if (isEditMode) {
        // Update existing customer - funktioniert für alle Kundentypen
        dispatch(updateCustomer({
          id,
          user: {
            name: formData.name,
            phoneNumber: formData.phoneNumber,
            address: formData.address
          }
        })).then((result) => {
          if (!result.error) {
            navigate(`/kunden/${id}`);
          }
        });
      } else {
        // Create new phone customer - Admin Web App erstellt nur Telefonkunden
        dispatch(createCustomer({
          isPhoneCustomer: true, // Immer true für Kunden, die im Admin-Panel erstellt werden
          name: formData.name,
          phoneNumber: formData.phoneNumber,
          address: formData.address
        })).then((result) => {
          if (!result.error) {
            navigate('/kunden');
          }
        });
      }
    }
  };
  
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/kunden/${id}`);
    } else {
      navigate('/kunden');
    }
  };
  
  if (isLoading && isEditMode) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={error}
        onRetry={() => isEditMode && dispatch(fetchCustomerById(id))}
      />
    );
  }

  if ((!isEditMode && !canCreateCustomer) || (isEditMode && currentCustomer && !canEditCustomer)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Keine Berechtigung zum {isEditMode ? 'Bearbeiten dieses Kunden' : 'Anlegen von Kunden'}.
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isEditMode ? 'Kunde bearbeiten' : 'Neuen Telefonkunden anlegen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Kunden', path: '/kunden' },
          { label: isEditMode ? 'Bearbeiten' : 'Telefonkunde hinzufügen', path: isEditMode ? `/kunden/${id}/bearbeiten` : '/kunden/neu' }
        ]}
      />
      
      <form onSubmit={handleSubmit}>
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Kunden-Informationen
          </Typography>
          <Grid container spacing={3}>
            {!isEditMode && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary">
                    Telefonkunden-Erfassung
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Hier können Sie Telefonkunden ohne E-Mail und Passwort erfassen, die über Telefon bestellen.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="name"
                name="name"
                label="Vollständiger Name"
                value={formData.name}
                onChange={handleChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="phoneNumber"
                name="phoneNumber"
                label="Telefonnummer"
                value={formData.phoneNumber}
                onChange={handleChange}
                error={!!formErrors.phoneNumber}
                helperText={formErrors.phoneNumber}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="address"
                name="address"
                label="Anschrift (optional)"
                value={formData.address}
                onChange={handleChange}
                error={!!formErrors.address}
                helperText={formErrors.address}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </FormPaper>
        
        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isLoading}
          >
            {isEditMode ? 'Änderungen speichern' : 'Kunde erstellen'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CustomerForm;
